/**
 * Shared lens contract.
 *
 * Every visualization lens (force, sankey, timeline, matrix, tree, radar)
 * implements the same renderer interface and ships its examples as
 * LensExample[]. This is what lets the gallery and viewer treat all lenses
 * uniformly: pick a renderer by lens type, hand it the example's data, render,
 * export. Each lens owns its own data shape (the build() return), so the
 * contract intentionally keeps that side untyped (`unknown`) and each renderer
 * narrows it internally.
 */

import type { LensType } from "./types.js";
import type { ThemeName, Theme } from "./theme.js";

export interface LensExample {
  id: string;
  title: string;
  blurb: string;
  /** Short capability tags shown on the gallery card. */
  highlights: string[];
  lens: LensType;
  /** Returns the lens-specific data object this example renders. */
  build: () => unknown;
}

export interface LensRendererOptions {
  width?: number;
  height?: number;
  theme?: ThemeName | Theme;
  /** Render text labels (lenses that have optional labels honor this). */
  showLabels?: boolean;
}

/**
 * Common renderer surface. Mirrors ForceRenderer: construct against an <svg> or
 * host element, render data, optionally settle a layout synchronously (for
 * headless SVG export), serialize, and tear down.
 */
export interface LensRenderer {
  render(data: unknown): LensRenderer;
  exportSVG(): string;
  /** Synchronous settle for headless export; no-op for static lenses. */
  settle?(ticks?: number): LensRenderer;
  destroy(): void;
}

export type LensRendererCtor = new (
  container: SVGSVGElement | HTMLElement,
  opts?: LensRendererOptions,
) => LensRenderer;

/** Human-facing metadata for each lens type (gallery section headings). */
export const LENS_INFO: Record<LensType, { label: string; tagline: string }> = {
  force: {
    label: "Force-Directed",
    tagline: "Relationship topology, clustering, and structural gaps.",
  },
  sankey: {
    label: "Sankey / Flow",
    tagline: "Value flow, throughput, and conversion through a system.",
  },
  timeline: {
    label: "Timeline / Swimlane",
    tagline: "Temporal progression across parallel streams.",
  },
  matrix: {
    label: "Matrix / Heatmap",
    tagline: "Cross-dimensional comparison and coverage.",
  },
  tree: {
    label: "Tree / Hierarchy",
    tagline: "Organizational structure and taxonomies.",
  },
  radar: {
    label: "Radar / Spider",
    tagline: "Multi-attribute comparison and capability profiles.",
  },
};
